import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTable, useGlobalFilter, useSortBy } from "react-table";
import {
  MenuItem,
  Select,
  ListSubheader,
  ThemeProvider,
} from "@mui/material";
import { selectUser } from "../../features/userSlice";
import JobActionModal from "../../components/SBCF/SalesSchedule/JobActionModal";
import { Stepper, Step, StepLabel, Typography } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import FolderIcon from "@mui/icons-material/Folder";
import SearchIcon from "@mui/icons-material/Search";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";
import { EmptyState } from "../../components/UI/States";
import { Segmented } from "../../components/SBCF/ui";

// The schedule feed sends missing dates as null or a 1970/1900 "zero" date,
// which would render as Dec 31, 1969 — treat those as no date.
const parseScheduleDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getFullYear() <= 1970) return null;
  return date;
};

// Newest first: orders by Order Date, quotes by Quote In.
const jobTime = (job) => {
  const [primary, fallback] =
    job.Status === "Quote"
      ? [job["Quote In"], job["Order Date"]]
      : [job["Order Date"], job["Quote In"]];
  const date = parseScheduleDate(primary) || parseScheduleDate(fallback);
  return date ? date.getTime() : 0;
};

const GlobalFilter = ({ globalFilter, setGlobalFilter }) => (
  <div className="relative w-full md:max-w-sm">
    <SearchIcon
      fontSize="small"
      className="app-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
    />
    <input
      value={globalFilter || ""}
      onChange={(e) => setGlobalFilter(e.target.value || undefined)}
      placeholder="Search jobs..."
      aria-label="Search jobs"
      className="app-input w-full rounded-xl border py-2.5 pl-9 pr-3 text-sm"
    />
  </div>
);

const SalesScheduleTable = ({
  data,
  onAction,
  currentPage,
  pageSize,
  setPageSize,
  setCurrentPage,
  viewType,
}) => {
  const formatDate = (value) => {
    const date = parseScheduleDate(value);
    if (!date) return "N/A";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(date);
  };

  const getStepStatusLabel = (stepIndex, currentStep) => {
    if (stepIndex < currentStep) return "Completed";
    if (stepIndex === currentStep) return "In Progress";
    return "Awaiting";
  };

  const DesignStepper = ({ job }) => {
    const steps = ["Initial Design", "Sales Approval", "Final Designs"];
    const conditions = [
      Boolean(job["Date Designed"]),
      Boolean(job["Approved"]),
      Boolean(job["To Production Date"]),
    ];

    const currentStep = conditions.findIndex((c) => !c);
    const activeStep = currentStep === -1 ? steps.length : currentStep;

    const dateMap = [
      job["Date Designed"],
      job["Approved Date"] || job["Approved"], // fallback if using boolean
      job["To Production Date"],
    ];

    const getLabel = (index) => {
      if (index < activeStep && dateMap[index]) {
        const date = new Date(dateMap[index]);
        return date.toLocaleDateString();
      }
      if (index === activeStep) return "Awaiting";
      return "Not Started";
    };

    return (
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label, i) => {
          let color;
          if (activeStep === i) {
            color = "primary.main"; // blue
          } else if (i < activeStep) {
            color = "success.main"; // green
          } else {
            color = "warning.main"; // yellow
          }

          return (
            <Step key={label} completed={i < activeStep}>
              <StepLabel
                sx={{
                  "& .MuiStepIcon-root": {
                    color: color,
                  },
                  "& .MuiStepIcon-root.Mui-completed": {
                    color: "success.main",
                  },
                }}
                optional={
                  <Typography variant="caption" color="textSecondary">
                    {getLabel(i)}
                  </Typography>
                }
              >
                {label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    );
  };

  const ProductionStepper = ({ job }) => {
    const designComplete =
      Boolean(job["Date Designed"]) &&
      Boolean(job["Approved"]) &&
      Boolean(job["To Production Date"]);

    const steps = ["Cut", "Built", "Delivered"];

    const conditions = [
      Boolean(job["Cut"]),
      Boolean(job["Built"]),
      Boolean(job["Delivered"]),
    ];

    const dateMap = [
      job["Cut - Date"],
      job["Built - Date"],
      job["Delivered - Date"],
    ];

    const currentStep = designComplete ? conditions.findIndex((c) => !c) : 0;
    const activeStep =
      designComplete && currentStep === -1 ? steps.length : currentStep;

    const getLabel = (index) => {
      if (!designComplete) return "Not Started";
      if (index < activeStep && dateMap[index]) {
        const date = new Date(dateMap[index]);
        return <>{date.toLocaleDateString()}</>;
      }
      if (index === activeStep) {
        return "Awaiting";
      }
      return "Awaiting";
    };

    return (
      <Stepper
        sx={{ width: "100%", flexGrow: 1 }}
        activeStep={designComplete ? activeStep : 0}
        alternativeLabel
      >
        {steps.map((label, i) => {
          const isFinalDesignsDone = Boolean(job["To Production Date"]);
          const isComplete = i < activeStep;
          const isActive = i === activeStep;

          let color;
          if (!isFinalDesignsDone && i === 0) {
            color = "warning.main"; // Yellow until designs are done
          } else if (isComplete) {
            color = "success.main"; // Green for completed
          } else if (isActive) {
            color = "primary.main"; // Blue for current
          } else {
            color = "warning.main"; // Yellow for upcoming
          }

          return (
            <Step key={label} completed={isComplete}>
              <StepLabel
                sx={{
                  flex: 1,
                  textAlign: "center",
                  "& .MuiStepIcon-root": { color },
                  "& .MuiStepIcon-root.Mui-completed": {
                    color: "success.main",
                  },
                }}
                optional={
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ whiteSpace: "pre-line" }}
                  >
                    {getLabel(i)}
                  </Typography>
                }
              >
                {label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    );
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "Job #",
        accessor: "Job #",
        Cell: ({ row }) => {
          const {
            "Job #": JobNumber,
            "Order Date": OrderDate,
            "Sch Del Date": deliveryDate,
            Status: status,
          } = row.original;

          return (
            <div className="space-y-1">
              <div>
                <button
                  type="button"
                  onClick={() => onAction(JobNumber, 1)}
                  className="app-link tabular-nums"
                >
                  {JobNumber}
                </button>
                {status && (
                  <>
                    {status !== "Quote" && (
                      <>
                        <div className="app-muted whitespace-nowrap text-xs tabular-nums">
                          Order Date: {formatDate(OrderDate)}
                        </div>
                        <div className="app-muted flex flex-col whitespace-nowrap text-xs tabular-nums">
                          Delivery Date: {formatDate(deliveryDate)}
                        </div>
                      </>
                    )}
                    <div className="-ml-2 flex gap-0.5 pt-1">
                      <button
                        type="button"
                        onClick={() => onAction(JobNumber, 0)}
                        className="app-icon-btn"
                        title="Send Email"
                        aria-label={`Send email about job ${JobNumber}`}
                      >
                        <EmailIcon fontSize="small" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onAction(JobNumber, 1)}
                        className="app-icon-btn"
                        title="View Files"
                        aria-label={`View files for job ${JobNumber}`}
                      >
                        <FolderIcon fontSize="small" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        },
      },
      {
        Header: "Job Info",
        accessor: "Customer", // base accessor, but not used directly
        Cell: ({ row }) => {
          const { Customer, "Job Name": JobName, Address } = row.original;
          return (
            <div className="space-y-1">
              <div className="font-semibold">{Customer}</div>
              <div className="app-muted">{JobName}</div>
              {Address ? (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(
                    Address
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-link text-sm"
                >
                  {Address}
                </a>
              ) : (
                <div className="app-muted text-sm">N/A</div>
              )}
            </div>
          );
        },
      },

      {
        Header: "Designers",
        accessor: "Estimator", // not directly used
        Cell: ({ row }) => {
          const { Estimator, Designer } = row.original;
          return (
            <div className="space-y-1">
              <div>
                <span className="font-medium">Estimator:</span>{" "}
                <span className="app-muted">{Estimator || "—"}</span>
              </div>
              <div>
                <span className="font-medium">Designer:</span>{" "}
                <span className="app-muted">{Designer || "—"}</span>
              </div>
            </div>
          );
        },
      },
    ];

    if (viewType === "Quote") {
      baseColumns.push(
        {
          Header: "Quote In",
          accessor: "Quote In",
          Cell: ({ value }) => formatDate(value),
        },
        {
          Header: "Quote By Date",
          accessor: "Quote By Date",
          Cell: ({ value }) => formatDate(value),
        },
        {
          Header: "Quote Done",
          accessor: "Quote Done",
          Cell: ({ value }) => formatDate(value),
        }
      );
    } else if (viewType === "Order") {
      baseColumns.push(
        {
          Header: "Design Status",
          accessor: "DesignProgress",
          Cell: ({ row }) => <DesignStepper job={row.original} />,
        },
        {
          Header: "Production Status",
          accessor: "ProductionProgress",
          Cell: ({ row }) => <ProductionStepper job={row.original} />,
        }
      );
    }

    return baseColumns;
  }, [viewType, onAction]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
  } = useTable({ columns, data }, useGlobalFilter, useSortBy);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize]);

  const totalPages = Math.ceil(rows.length / pageSize);

  return (
    <div className="flex w-full flex-col">
      <div className="mb-4 flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <GlobalFilter
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
        <div className="flex shrink-0 items-center gap-2">
          <label htmlFor="pageSize" className="app-muted text-sm">
            Rows per page:
          </label>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            size="small"
            inputProps={{ id: "pageSize" }}
          >
            {[10, 25, 50, 100].map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
      <div className="app-card overflow-hidden rounded-2xl border">
        <div className="relative max-h-[800px] overflow-auto">
          <table {...getTableProps()} className="app-table table-auto">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className="sticky top-0 z-10 cursor-pointer select-none backdrop-blur-md"
                    >
                      {column.render("Header")}
                      <span className="ml-1">
                        {column.isSorted
                          ? column.isSortedDesc
                            ? "▼"
                            : "▲"
                          : ""}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {paginatedRows.length === 0 && (
                <tr>
                  <td colSpan={columns.length}>
                    <EmptyState
                      compact
                      icon={EventBusyIcon}
                      title="No jobs to show"
                      description="Nothing matches this view. Try another salesperson, tab, or search."
                    />
                  </td>
                </tr>
              )}
              {paginatedRows.map((row, rowIndex) => {
                prepareRow(row);
                const isOnHold = row.original["On Hold"];
                const holdReason = row.original["Hold Reason"];

                return (
                  <tr
                    {...row.getRowProps()}
                    className={isOnHold ? "bg-amber-500/10" : ""}
                  >
                    {row.cells.map((cell, cellIndex) => {
                      const isFirstCell = cellIndex === 0;
                      return (
                        <td
                          {...cell.getCellProps()}
                          className="!align-top"
                        >
                          <div className="flex flex-col items-start justify-between">
                            <span>{cell.render("Cell")}</span>
                            {isFirstCell && isOnHold && (
                              <span className="mt-2 flex whitespace-nowrap rounded-full bg-rose-500/15 px-2.5 py-0.5 text-xs font-medium text-rose-700 dark:text-rose-300">
                                On Hold: {holdReason}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="app-btn app-btn-secondary"
        >
          Previous
        </button>
        <span className="app-muted text-sm tabular-nums">
          Page {currentPage} of {totalPages}
        </span>
        <button
          type="button"
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
          className="app-btn app-btn-secondary"
        >
          Next
        </button>
      </div>
    </div>
  );
};

const getSalesTeam = async () => {
  try {
    const response = await fetch("/api/getUserID");
    const data = await response.json();
    const team = data.recordset.filter(
      (person) =>
        (person.Name !== "JACK EARLEY" && person.JobTitle === "Inside Sales") ||
        person.JobTitle === "Outside Sales" ||
        person.Name === "BLAKE UPHAM"
    );
    return team;
  } catch (error) {
    console.error("Error fetching sales team:", error);
    return [];
  }
};

const SalesSchedule = () => {
  const user = useSelector(selectUser);
  // MUI's own palette can't see Tailwind's `dark:` classes, so the Selects,
  // Steppers, and StepLabels here would keep their light-theme (near-black)
  // text on the dark card. Feed the in-app toggle into a MUI theme instead.
  const muiTheme = useAppMuiTheme();
  const [userID, setUserID] = useState(null);
  const [branch, setBranch] = useState("");
  const [schedule, setSchedule] = useState([]);
  const [salesTeam, setSalesTeam] = useState([]);
  const [selectedTable, setSelectedTable] = useState("Active");
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedTab, setSelectedTab] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [viewType, setViewType] = useState("Quote");

  useEffect(() => {
    let retryInterval;

    const fetchSalesTeam = async () => {
      const team = await getSalesTeam();
      if (team.length > 0) {
        setSalesTeam(team);

        if (user?.userID) {
          setUserID(user.userID);

          // Default branch = current user’s branch
          const currentUserBranch = team.find(
            (m) => m.UserID === user.userID
          )?.Branch;
          if (currentUserBranch) setBranch(currentUserBranch);
        }

        clearInterval(retryInterval);
      }
    };

    fetchSalesTeam();
    retryInterval = setInterval(fetchSalesTeam, 2000);

    return () => clearInterval(retryInterval);
  }, [user]);

  useEffect(() => {
    const getSchedule = async () => {
      if (!userID) return;
      const response = await fetch(`/api/sbcf/SalesSchedule?id=${userID}`);
      const data = await response.json();
      if (data.recordset) setSchedule(data.recordset);
    };
    getSchedule();
  }, [userID]);

  const filteredData = useMemo(() => {
    const filtered = schedule.filter((item) => {
      const matchesStatus = item.Status === viewType;
      const isActive = !item.Canceled && !item.BLT && !item["On Hold"];
      switch (selectedTable) {
        case "Active":
          return matchesStatus && isActive;
        case "Canceled":
          return matchesStatus && item.Canceled;
        case "Completed":
          return matchesStatus && item["Completed"];
        default:
          return matchesStatus && isActive;
      }
    });
    filtered.sort((a, b) => jobTime(b) - jobTime(a));
    setCurrentPage(1);
    return filtered;
  }, [schedule, selectedTable, viewType]);

  // Group salesTeam by branch
  const groupedSalesTeam = salesTeam.reduce((acc, member) => {
    const { Branch } = member;
    if (!acc[Branch]) acc[Branch] = [];
    acc[Branch].push(member);
    return acc;
  }, {});

  const branches = Object.keys(groupedSalesTeam);
  const branchMembers = groupedSalesTeam[branch] || [];

  // MUI's Select warns (and walks child.props) whenever `value` matches none of
  // its options, so only hand it a value once the matching MenuItem exists.
  // userID itself is left alone — the schedule fetch still uses it.
  const branchValue = branches.includes(branch) ? branch : "";
  const userValue = branchMembers.some((m) => m.UserID === userID)
    ? userID
    : "";

  return (
    <ThemeProvider theme={muiTheme}>
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          ariaLabel="Quotes or orders"
          value={viewType}
          onChange={setViewType}
          options={["Quote", "Order"].map((type) => ({
            value: type,
            label: `${type}s`,
          }))}
        />

        <div className="flex w-full flex-wrap gap-2 sm:ml-auto sm:w-auto">
        {/* Branch Select */}
        <Select
          value={branchValue}
          onChange={(e) => {
            setBranch(e.target.value);
            setUserID(""); // reset user when branch changes
          }}
          className="min-w-[200px] flex-1 sm:flex-none"
          size="small"
          inputProps={{ "aria-label": "Branch" }}
        >
          {branches.map((b) => (
            <MenuItem key={b} value={b}>
              {b}
            </MenuItem>
          ))}
        </Select>

        {/* User Select */}
        <Select
          value={userValue}
          onChange={(e) => setUserID(e.target.value)}
          className="min-w-[200px] flex-1 sm:flex-none"
          size="small"
          inputProps={{ "aria-label": "Salesperson" }}
        >
          {/* No `branch && ...` guard: a falsy branch would render the empty
              string as a child, and MUI reads `child.props.value` off it. */}
          {branchMembers.map((item) => (
            <MenuItem key={item.UserID} value={item.UserID}>
              {item.Name}
            </MenuItem>
          ))}
        </Select>
        </div>
      </div>

      <Segmented
        ariaLabel="Job status"
        value={selectedTable}
        onChange={setSelectedTable}
        options={["Active", "Canceled", "Completed"].map((tab) => ({
          value: tab,
          label: tab,
        }))}
      />

      <SalesScheduleTable
        data={filteredData}
        onAction={(jobID, tab) => {
          setSelectedJob(jobID);
          setSelectedTab(tab);
        }}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        viewType={viewType}
      />

      <JobActionModal
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />
    </div>
    </ThemeProvider>
  );
};

export default SalesSchedule;
